import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import StudentLayout from '../../components/layouts/StudentLayout';
import AdminLayout from '../../components/layouts/AdminLayout';
import { studentService } from '../../services/studentService';
import { getAssessmentNumber } from '../../utils/assessmentNumbers';
import { downloadFormativeResultSlipPdf } from '../formative-assessment-management/utils/marksheetPdf';
import StudentSummativeExamDashboard from './components/StudentSummativeExamDashboard';
import StudentResitExamDashboard from './components/StudentResitExamDashboard';
import DepartmentSummativeExamDashboard from './components/DepartmentSummativeExamDashboard';
import ProvisionalResults from './components/ProvisionalResults';
import AcademicProgress from './components/AcademicProgress';
import { 
  BookOpen, Calendar, Download, Filter, CheckCircle2, Clock, Search, 
  Award, FileText, BarChart3, Layers, ChevronRight, AlertCircle, RefreshCw, FileSpreadsheet 
} from 'lucide-react';

const SummativeAssessmentPortal = ({ embedded = false }) => {
  const { profile } = useAuth();
  const [searchParams] = useSearchParams();
  const examCardRequested = searchParams.get('view') === 'exam-cards';
  const resultsRequested = searchParams.get('view') === 'results';
  const formativeRequested = searchParams.get('view') === 'formative';
  const historyRequested = searchParams.get('view') === 'history';
  const resitRequested = searchParams.get('view') === 'resits';
  const isStudent = profile?.primary_role === 'student' || !profile?.primary_role;
  // Formative marks are available independently of the approval workflow, so
  // load the same student assessment data when the student opens that view.
  const needsStudentResultsData = isStudent && (examCardRequested || resultsRequested || formativeRequested);

  const [loading, setLoading] = useState(true);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [examDownloading, setExamDownloading] = useState(false);
  const [studentRecord, setStudentRecord] = useState(null);
  const [academicYears, setAcademicYears] = useState([]);
  const [semesters, setSemesters] = useState([]);
  const [unitRegistrations, setUnitRegistrations] = useState([]);
  const [examBookings, setExamBookings] = useState([]);
  const [timelines, setTimelines] = useState([]);
  const [assessments, setAssessments] = useState([]);
  const [institutionSettings, setInstitutionSettings] = useState(null);
  const [feeBalance, setFeeBalance] = useState(null);
  const [feeCheckComplete, setFeeCheckComplete] = useState(false);
  const [billingStatus, setBillingStatus] = useState(null);

  // Filters
  const [selectedYearId, setSelectedYearId] = useState('all');
  const [selectedSemesterId, setSelectedSemesterId] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('formative'); // 'formative', 'summary'
  const examCardRef = React.useRef(null);
  const downloadExamCard = async () => {
    if (!examCardRef.current) return;
    setExamDownloading(true);
    await new Promise(resolve => requestAnimationFrame(resolve));
    try {
      const canvas = await html2canvas(examCardRef.current, { scale: 2, backgroundColor: '#ffffff', useCORS: true });
      const pdf = new jsPDF('p', 'mm', 'a4');
      const width = 190;
      pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 10, 10, width, (canvas.height * width) / canvas.width);
      pdf.save(`exam-card-${studentRecord?.admission_number || 'student'}.pdf`);
    } finally { setExamDownloading(false); }
  };

  useEffect(() => {
    if (profile?.id && needsStudentResultsData) {
      loadAssessmentData();
    } else if (profile?.id) {
      setLoading(false);
    }
  }, [profile?.id, needsStudentResultsData]);

  const loadAssessmentData = async () => {
    setLoading(true);
    try {
      const studentId = profile.id;

      // 1. Fetch student record via studentService (same service as Student Portal Dashboard)
      let stData = null;
      try {
        stData = await studentService.getStudentById(studentId);
      } catch (e) {
        console.warn('Error fetching student record via studentService:', e);
      }

      if (!stData) {
        try {
          const { data: st } = await supabase
            .from('students')
            .select(`
              *,
              program:programs(
                id, name, code,
                department:departments(
                  id, name, code,
                  faculty:faculties(id, name, code)
                )
              )
            `)
            .or(`profile_id.eq.${studentId},id.eq.${studentId}`)
            .maybeSingle();

          if (st) stData = st;
        } catch (e) {}
      }

      if (stData) setStudentRecord(stData);
      const { data: currentEnrollment } = await supabase.from('student_semester_enrollments').select('status').eq('student_id', stData?.id || studentId).order('created_at', { ascending: false }).limit(1).maybeSingle();
      const { data: postedBill } = await supabase.from('student_billing_requests').select('status').eq('student_id', stData?.id || studentId).eq('status', 'posted').limit(1).maybeSingle();
      setBillingStatus(postedBill?.status === 'posted' ? 'billed' : (currentEnrollment?.status || null));
      const storedBalance = stData?.fee_balance ?? stData?.fees_balance ?? stData?.outstanding_balance;
      if (storedBalance !== null && storedBalance !== undefined) setFeeBalance(Number(storedBalance));
      try {
        const statement = await studentService.getStudentStatement(stData?.id || studentId);
        const rows = Array.isArray(statement?.data) ? statement.data : (statement?.data?.transactions || []);
        let balance = rows.reduce((total, row) => total + Number(row.debit_amount ?? row.debit ?? 0) - Number(row.credit_amount ?? row.credit ?? 0), 0);
        const ledgerIds = [stData?.id, stData?.profile_id, studentId].filter(Boolean);
        const { data: ledger } = await supabase.from('student_ledger').select('*').in('student_id', ledgerIds);
        if (ledger?.length) balance = ledger.reduce((total, row) => total + Number(row.debit_amount ?? row.debit ?? row.amount ?? 0) - Number(row.credit_amount ?? row.credit ?? 0), 0);
        setFeeBalance(balance);
      } catch (_) { setFeeBalance(null); }
      finally { setFeeCheckComplete(true); }

      // 2. Fetch Institution Settings
      try {
        const { data: inst } = await supabase
          .from('institution_settings')
          .select('*')
          .limit(1)
          .maybeSingle();
        if (inst) setInstitutionSettings(inst);
      } catch (e) {}

      // 3. Fetch Academic Years & Semesters
      try {
        const [{ data: yData }, { data: sData }] = await Promise.all([
          supabase.from('academic_years').select('*').order('start_date', { ascending: false }),
          supabase.from('semesters').select('*').order('start_date', { ascending: false })
        ]);
        if (yData) {
          setAcademicYears(yData);
          const activeYear = yData.find(y => String(y.status).toLowerCase() === 'active') || yData[0];
          if (activeYear) setSelectedYearId(activeYear.id);
        }
        if (sData) {
          setSemesters(sData);
          const activeSem = sData.find(s => String(s.status).toLowerCase() === 'active') || sData[0];
          if (activeSem) setSelectedSemesterId(activeSem.id);
        }
      } catch (e) {
        console.warn('Error fetching period context:', e);
      }

      // 4. Fetch Student Unit Registrations
      const candidateSids = [studentId, stData?.admission_number, profile?.student_id].filter(Boolean);
      let regRows = [];
      try {
        let regQuery = supabase
          .from('student_unit_registrations')
          .select(`
            id, student_id, unit_id, academic_year_id, semester_id, enrollment_status, is_active,
            review_status, resolution_type, grade, grade_letter,
            unit:units(id, code, name, credit_hours, classification),
            academic_year:academic_years(id, name),
            semester:semesters(id, name, start_date, end_date)
          `);

        if (candidateSids.length === 1) {
          regQuery = regQuery.eq('student_id', candidateSids[0]);
        } else {
          regQuery = regQuery.in('student_id', candidateSids);
        }

        const { data: directRegs } = await regQuery;
        if (directRegs && directRegs.length > 0) {
          regRows = directRegs;
        } else {
          // Fallback select all and match
          const { data: allRegs } = await supabase
            .from('student_unit_registrations')
            .select(`
              id, student_id, unit_id, academic_year_id, semester_id, enrollment_status, is_active,
              review_status, resolution_type, grade, grade_letter,
              unit:units(id, code, name, credit_hours, classification),
              academic_year:academic_years(id, name),
              semester:semesters(id, name, start_date, end_date)
            `);
          if (allRegs) {
            regRows = allRegs.filter(r => candidateSids.some(s => String(r.student_id).toLowerCase() === String(s).toLowerCase()));
          }
        }
      } catch (e) {
        console.warn('Error fetching unit registrations:', e);
      }

      setUnitRegistrations(regRows || []);

      // An exam card must list only units for which the student has actually
      // created a summative examination booking. Unit registration alone does
      // not qualify a unit for the card.
      let bookingRows = [];
      try {
        const { data: bookedRows } = await supabase
          .from('summative_exam_bookings')
          .select('id, unit_registration_id, unit_id, academic_year_id, semester_id, booking_status')
          .eq('student_id', studentId)
          .eq('booking_status', 'booked');
        bookingRows = bookedRows || [];
      } catch (e) {
        console.warn('Error fetching booked examination units:', e);
      }
      setExamBookings(bookingRows);

      // 5. Fetch Assessment Timelines for registered units
      const unitIds = [...new Set((regRows || []).map(r => r.unit_id).filter(Boolean))];
      let tRows = [];
      if (unitIds.length > 0) {
        try {
          const { data: tData } = await supabase
            .from('assessment_timelines')
            .select('*')
            .in('unit_id', unitIds)
            .eq('assessment_type', 'formative')
            .order('assessment_number', { ascending: true });
          if (tData) tRows = tData;
        } catch (e) {}
      }
      setTimelines(tRows);

      // 6. Fetch Student Assessment Marks via RPC & Direct Queries
      let markRows = [];
      try {
        // Try SECURITY DEFINER RPC first to bypass RLS mismatch
        const { data: rpcData, error: rpcErr } = await supabase.rpc('get_student_formative_marks', { p_student_id: studentId });
        
        if (!rpcErr && rpcData) {
          if (rpcData.student) {
            let student = rpcData.student;
            // Fetch nested program, department, and faculty if program_id exists
            if (student.program_id) {
              try {
                const { data: progData } = await supabase
                  .from('programs')
                  .select('name, department:departments(name, faculty:faculties(name))')
                  .eq('id', student.program_id)
                  .single();
                
                if (progData) {
                  student = {
                    ...student,
                    program_name: progData.name,
                    department_name: progData.department?.name,
                    faculty_name: progData.department?.faculty?.name,
                    program: progData
                  };
                }
              } catch (e) {
                console.warn('Error fetching student nested program data', e);
              }
            }
            setStudentRecord(prev => ({ ...prev, ...student }));
          }
          if (rpcData.registrations && rpcData.registrations.length > 0) {
            // The SECURITY DEFINER RPC provides rows when the direct query is
            // restricted by RLS, but it intentionally returns only the core
            // registration fields. Merge it with the direct rows so HOD
            // resolution metadata (and the richer nested period data) is not
            // lost before the portal applies its eligibility filters.
            const directById = new Map((regRows || []).map(row => [String(row.id), row]));
            const rpcIds = new Set();
            const mergeNested = (direct, rpc) => {
              const merged = { ...(direct || {}), ...(rpc || {}) };
              Object.keys(merged).forEach(key => {
                if ((merged[key] === null || merged[key] === undefined || merged[key] === '') && direct?.[key] != null) {
                  merged[key] = direct[key];
                }
              });
              return merged;
            };
            const mergedRpcRows = rpcData.registrations.map(row => {
              const direct = directById.get(String(row.id));
              rpcIds.add(String(row.id));
              return {
                ...(direct || {}),
                ...row,
                unit: mergeNested(direct?.unit, row.unit),
                academic_year: mergeNested(direct?.academic_year, row.academic_year),
                semester: mergeNested(direct?.semester, row.semester)
              };
            });
            // Keep any directly visible rows that the RPC did not return.
            regRows = [
              ...mergedRpcRows,
              ...(regRows || []).filter(row => !rpcIds.has(String(row.id)))
            ];
            setUnitRegistrations(regRows);
          }
          if (rpcData.timelines && rpcData.timelines.length > 0) {
            setTimelines(rpcData.timelines);
          }
          if (rpcData.assessments && rpcData.assessments.length > 0) {
            markRows = rpcData.assessments;
          }
        }

        // Direct table query fallback / merge
        const regIds = regRows.map(r => r.id).filter(Boolean);
        const fallbackSelect = '*, evidence:assessment_evidence(id)';
        const [byStudent, byReg, byRecent] = await Promise.all([
          candidateSids.length ? supabase.from('student_assessments').select(fallbackSelect).in('student_id', candidateSids) : { data: [] },
          regIds.length ? supabase.from('student_assessments').select(fallbackSelect).in('unit_registration_id', regIds) : { data: [] },
          supabase.from('student_assessments').select(fallbackSelect).order('created_at', { ascending: false }).limit(300)
        ]);

        const dedup = new Map();
        const allFetched = [
          ...markRows,
          ...(byRecent.data || []),
          ...(byReg.data || []),
          ...(byStudent.data || [])
        ];
        allFetched.forEach(item => {
          if (!item?.id) return;
          const key = String(item.id);
          const previous = dedup.get(key);
          const merged = { ...(previous || {}), ...item };

          // The SECURITY DEFINER RPC knows whether an evidence row exists,
          // while the direct table fallback may not expose that relation under
          // the student's RLS policy. Do not let the fallback erase the
          // approval/evidence state used to release marks after trainer review.
          merged.has_evidence = Boolean(
            previous?.has_evidence ||
            item.has_evidence ||
            previous?.evidence?.length ||
            item.evidence?.length
          );

          // Preserve scoped identity fields when the fallback row does not
          // contain the RPC's derived values.
          ['unit_id', 'timeline_id', 'unit_registration_id', 'assessment_number'].forEach(field => {
            if ((merged[field] === null || merged[field] === undefined) && previous?.[field] !== undefined) {
              merged[field] = previous[field];
            }
          });

          if ((!merged.evidence || merged.evidence.length === 0) && previous?.evidence?.length) {
            merged.evidence = previous.evidence;
          }
          dedup.set(key, merged);
        });
        markRows = Array.from(dedup.values());

        // The POE workflow RPC is the authoritative source for evidence
        // status. It is SECURITY DEFINER and therefore still works when the
        // student's direct evidence relation is hidden by RLS. In particular,
        // trainer approval moves the item to awaiting_department, which must
        // count as POE present rather than NP.
        try {
          const { data: poeItems, error: poeError } = await supabase.rpc('get_poe_workflow_items');
          if (!poeError && Array.isArray(poeItems)) {
            const poeByAssessment = new Map(
              poeItems
                .filter(item => item?.assessment_id)
                .map(item => [String(item.assessment_id), item])
            );

            markRows = markRows.map(mark => {
              const poe = poeByAssessment.get(String(mark.id));
              if (!poe) return mark;
              return {
                ...mark,
                has_evidence: Boolean(mark.has_evidence || poe.evidence_id),
                evidence_id: poe.evidence_id || mark.evidence_id,
                evidence_workflow_stage: poe.workflow_stage || mark.evidence_workflow_stage,
                evidence_approval_status: poe.workflow_stage === 'approved'
                  ? 'approved'
                  : mark.evidence_approval_status
              };
            });

            // If table reads are restricted, the workflow RPC can still be
            // the only place the student's marked assessment is visible.
            const knownIds = new Set(markRows.map(mark => String(mark.id)));
            poeItems.forEach(poe => {
              if (!poe?.assessment_id || knownIds.has(String(poe.assessment_id))) return;
              markRows.push({
                id: poe.assessment_id,
                student_id: poe.student_id,
                unit_id: poe.unit_id,
                assessment_number: poe.assessment_number,
                raw_marks: poe.raw_marks,
                converted_marks: poe.converted_marks,
                has_evidence: Boolean(poe.evidence_id),
                evidence_id: poe.evidence_id,
                evidence_workflow_stage: poe.workflow_stage
              });
            });
          }
        } catch (poeErr) {
          console.warn('Could not load student POE workflow state:', poeErr);
        }

        // Ensure we know if they have evidence
        const missingEvidenceIds = markRows.filter(m => m.evidence === undefined && m.has_evidence === undefined).map(m => m.id);
        if (missingEvidenceIds.length > 0) {
          try {
            const { data: evidenceData } = await supabase
              .from('assessment_evidence')
              .select('student_assessment_id')
              .in('student_assessment_id', missingEvidenceIds);
            
            const evidenceSet = new Set((evidenceData || []).map(e => String(e.student_assessment_id)));
            markRows = markRows.map(m => ({
              ...m,
              has_evidence: m.has_evidence ?? evidenceSet.has(String(m.id))
            }));
          } catch (e) {
            console.warn('Failed to fetch fallback evidence', e);
          }
        }
      } catch (e) {
        console.warn('Error fetching student_assessments:', e);
      }

      setAssessments(markRows);

    } catch (err) {
      console.error('Error loading assessment portal:', err);
    } finally {
      setLoading(false);
    }
  };

  // Filtered unit registrations based on selected year/semester & search
  const filteredRegistrations = useMemo(() => {
    const rawFiltered = unitRegistrations.filter(reg => {
      // A registration resolved by the HOD is retained in the database for
      // audit/history, but it is no longer an enrolled unit for the student's
      // result view. Do not let withdrawn/removed rows or resolved exceptions
      // reappear just because the formative RPC returns the full history.
      const enrollmentStatus = String(reg.enrollment_status || '').toLowerCase();
      const reviewStatus = String(reg.review_status || '').toLowerCase();
      const resolutionType = String(reg.resolution_type || '').toLowerCase();
      const hiddenEnrollmentStatuses = new Set(['withdrawn', 'removed', 'rejected', 'cancelled', 'deleted']);
      const hiddenReviewStatuses = new Set(['wrong_unit', 'already_completed', 'credit_transferred', 'elective_not_selected']);
      const hiddenResolutionTypes = new Set(['wrong_unit', 'already_completed', 'credit_transfer', 'credit_transferred', 'elective_not_selected']);
      if (hiddenEnrollmentStatuses.has(enrollmentStatus)) return false;
      if (hiddenReviewStatuses.has(reviewStatus) || hiddenResolutionTypes.has(resolutionType)) return false;
      // Waiting-list rows are not registered for teaching/assessment. Keep
      // completed rows available for historical periods unless they carry a
      // resolution marker (handled above).
      if (enrollmentStatus && !['enrolled', 'active', 'completed'].includes(enrollmentStatus)) return false;

      // Academic Year Filter
      if (selectedYearId !== 'all') {
        if (String(reg.academic_year_id) !== String(selectedYearId)) return false;
      }

      // Semester Filter
      if (selectedSemesterId !== 'all') {
        if (String(reg.semester_id) !== String(selectedSemesterId)) return false;
      }

      // Search term (Unit code or Unit name)
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        const codeMatch = reg.unit?.code?.toLowerCase().includes(term);
        const nameMatch = reg.unit?.name?.toLowerCase().includes(term);
        if (!codeMatch && !nameMatch) return false;
      }

      return true;
    });

    // Deduplicate by unit code / unit_id if viewing all academic years to prevent repeated units
    const deduplicated = new Map();
    rawFiltered.forEach(reg => {
      const key = (selectedYearId === 'all' && selectedSemesterId === 'all')
        ? (reg.unit?.code || reg.unit_id)
        : reg.id;
      if (key && !deduplicated.has(key)) {
        deduplicated.set(key, reg);
      }
    });

    return Array.from(deduplicated.values());
  }, [unitRegistrations, selectedYearId, selectedSemesterId, searchTerm]);

  const bookedExamRegistrations = useMemo(() => {
    const bookedRegistrationIds = new Set(
      examBookings
        .map(booking => booking.unit_registration_id)
        .filter(Boolean)
        .map(id => String(id)),
    );
    return filteredRegistrations.filter(registration =>
      bookedRegistrationIds.has(String(registration.id)),
    );
  }, [examBookings, filteredRegistrations]);

  // Map each registered unit to its CAM scores & Formative Total (/60)
  const unitFormativeData = useMemo(() => {
    return filteredRegistrations.map(reg => {
      const unitId = reg.unit_id;
      
      // Only use CAM windows declared for this registration's academic
      // period. Previously all unit timelines were used as a fallback, which
      // pulled CAMs from older periods into the current result slip and also
      // padded every unit with fabricated CAM columns. Calendar semesters are
      // the canonical period; legacy academic-semester rows are accepted only
      // when they match the registration directly (or overlap its dates).
      const registrationPeriodId = reg.semester_id || (selectedSemesterId !== 'all' ? selectedSemesterId : null);
      const registrationPeriod = semesters.find(s => String(s.id) === String(registrationPeriodId)) || reg.semester;
      const registrationStart = registrationPeriod?.start_date ? new Date(registrationPeriod.start_date) : null;
      const registrationEnd = registrationPeriod?.end_date ? new Date(registrationPeriod.end_date) : null;
      const rankedTimelines = timelines
        .filter(t => String(t.unit_id) === String(unitId))
        .map(t => {
          const calendarPeriodId = t.calendar_semester_id || null;
          const legacyPeriodId = t.semester_id || null;
          let rank = 0;

          if (registrationPeriodId) {
            if (calendarPeriodId && String(calendarPeriodId) === String(registrationPeriodId)) rank = 3;
            else if (legacyPeriodId && String(legacyPeriodId) === String(registrationPeriodId)) rank = 2;
            else if (!calendarPeriodId && registrationStart && registrationEnd && t.start_date && t.end_date) {
              const timelineStart = new Date(t.start_date);
              const timelineEnd = new Date(t.end_date);
              if (timelineStart <= registrationEnd && timelineEnd >= registrationStart) rank = 1;
            }
          } else if (!calendarPeriodId && !legacyPeriodId) {
            // With no period context, periodless legacy rows are the only
            // rows that can be shown without risking cross-semester leakage.
            rank = 1;
          }

          return { timeline: t, rank };
        })
        .filter(entry => entry.rank > 0)
        .sort((a, b) => {
          if (b.rank !== a.rank) return b.rank - a.rank;
          return Number(a.timeline.assessment_number || 0) - Number(b.timeline.assessment_number || 0);
        });

      const timelinesByNumber = new Map();
      rankedTimelines.forEach(({ timeline: t, rank }, index) => {
        const number = getAssessmentNumber(t, index + 1);
        const existing = timelinesByNumber.get(number);
        if (!existing || rank > existing.rank) timelinesByNumber.set(number, { timeline: t, rank });
      });
      let unitTimelines = Array.from(timelinesByNumber.values(), entry => entry.timeline);
      unitTimelines.sort((a, b) => {
        const aNum = getAssessmentNumber(a, 999);
        const bNum = getAssessmentNumber(b, 999);
        return aNum - bNum;
      });

      // Find marks per timeline with strict unit, registration and CAM-period
      // isolation. A unit can be registered again in a later semester, so a
      // mark linked only by unit/student is safe only when its timeline is one
      // of this period's CAMs. Timeline-less legacy marks are accepted only
      // when the student has a single registration for that unit; otherwise
      // there is no reliable way to assign them to a period.
      const periodTimelineIds = new Set(unitTimelines.map(t => String(t.id)));
      const sameUnitRegistrationCount = unitRegistrations.filter(row => String(row.unit_id) === String(unitId)).length;
      const studentKeys = [profile?.id, studentRecord?.admission_number, profile?.student_id]
        .filter(Boolean)
        .map(value => String(value).toLowerCase());
      const availableAssessments = assessments.filter(a => {
        const registrationMatch = String(a.unit_registration_id || '') === String(reg.id);
        if (registrationMatch) return true;
        const studentMatch = studentKeys.includes(String(a.student_id || '').toLowerCase());
        if (!studentMatch) return false;
        // The persisted timeline is authoritative for unit and period. Some
        // legacy rows store a unit code while the registration stores a UUID,
        // so do not reject a timeline-linked mark on that denormalised field.
        if (a.timeline_id) return periodTimelineIds.has(String(a.timeline_id));
        if (!a.unit_id || String(a.unit_id) !== String(unitId)) return false;
        return !a.unit_registration_id && sameUnitRegistrationCount <= 1;
      });

      const assignedAssessmentIds = new Set();

      const camMarks = unitTimelines.map((tl, index) => {
        const targetCamNum = getAssessmentNumber(tl, index + 1);
        let val = null;
        let isMissingPoe = false;

        // 1. Strict match by timeline_id
        let markObj = availableAssessments.find(a => !assignedAssessmentIds.has(a.id) && a.timeline_id && String(a.timeline_id) === String(tl.id));

        // 2. Match strictly by assessment_number
        if (!markObj) {
          markObj = availableAssessments.find(a => 
            !assignedAssessmentIds.has(a.id) && 
            Number(a.assessment_number) === targetCamNum &&
            // A persisted timeline link always wins. An assessment linked to
            // CAM3 must never be reused as CAM1 just because its legacy
            // assessment_number was left at the default value.
            (!a.timeline_id || String(a.timeline_id) === String(tl.id))
          );
        }

        // 3. Fallback: sequentially assign anonymous marks
        if (!markObj) {
          markObj = availableAssessments.find(a => 
            !assignedAssessmentIds.has(a.id) && 
            (!a.assessment_number || Number(a.assessment_number) === 0) &&
            // MUST be anonymous (no timeline) so we don't steal from CAM 3
            (!a.timeline_id) &&
            a.raw_marks !== null && a.raw_marks !== undefined
          );
        }

        if (markObj) {
          assignedAssessmentIds.add(markObj.id);
          const hasEvidence = markObj.has_evidence || (markObj.evidence && markObj.evidence.length > 0);
          const deadlineDate = tl.submission_deadline || tl.end_date;
          const isPastDeadline = deadlineDate && new Date(deadlineDate) < new Date();

          if (markObj.submission_status === 'verified' || markObj.submission_status === 'approved') {
            // Already approved, so ignore evidence requirements
            if (markObj.raw_marks !== null && markObj.raw_marks !== undefined) {
              val = Number(markObj.raw_marks);
            } else if (markObj.converted_marks !== null && markObj.converted_marks !== undefined) {
              val = Number(markObj.converted_marks);
            }
          } else if (isPastDeadline && !hasEvidence) {
            // Consequence of not uploading POE: Hide marks immediately
            val = 0; // Set to 0 so it counts towards the total
            isMissingPoe = true;
          } else {
            if (markObj.raw_marks !== null && markObj.raw_marks !== undefined) {
              val = Number(markObj.raw_marks);
            } else if (markObj.converted_marks !== null && markObj.converted_marks !== undefined) {
              val = Number(markObj.converted_marks);
            }
          }
        }

        return {
          id: tl.id,
          targetCamNum,
          title: tl.title || `CAM ${targetCamNum}`,
          marks: (val !== null && val !== undefined && !isNaN(val)) ? Math.min(30, Math.max(0, val)) : null,
          maxMarks: tl.total_marks || 30,
          isMissingPoe
        };
      });

      // Calculate totals
      const validMarks = camMarks.filter(c => c.marks !== null && c.marks !== undefined && !isNaN(c.marks));
      const rawSum = validMarks.reduce((acc, curr) => acc + curr.marks, 0);
      const maxRaw = unitTimelines.reduce((total, timeline) => total + (Number(timeline.total_marks) || 30), 0);

      // Scaled Formative Total out of 60 rounded to whole number
      const formativeTotal60 = maxRaw > 0 ? Math.round((rawSum / maxRaw) * 60) : 0;
      const hasMarks = validMarks.length > 0;

      return {
        registration: reg,
        unitCode: reg.unit?.code || 'UNIT',
        unitName: reg.unit?.name || 'Enrolled Unit',
        creditHours: reg.unit?.credit_hours || 3,
        academicYearName: reg.academic_year?.name || 'Academic Year',
        semesterName: reg.semester?.name || 'Semester',
        cams: camMarks,
        rawSum,
        maxRaw,
        formativeTotal60,
        hasMarks,
        status: hasMarks ? (formativeTotal60 >= 30 ? 'Satisfactory' : 'Unsatisfactory') : 'Pending'
      };
    });
  }, [filteredRegistrations, timelines, assessments, semesters, selectedSemesterId]);

  // Determine the maximum number of CAMs across all units to dynamically render columns
  const maxCamsCount = useMemo(() => {
    let max = 0;
    unitFormativeData.forEach(item => {
      if (item.cams && item.cams.length > max) {
        max = item.cams.length;
      }
      item.cams.forEach(c => {
        if (c.targetCamNum > max) max = c.targetCamNum;
      });
    });
    return Math.min(max, 10); // Cap at 10 to avoid UI breakage
  }, [unitFormativeData]);

  // Overall Statistics Summary
  const stats = useMemo(() => {
    const totalUnits = unitFormativeData.length;
    const unitsWithMarks = unitFormativeData.filter(u => u.hasMarks);
    const avgScore = unitsWithMarks.length > 0 
      ? Math.round(unitsWithMarks.reduce((acc, curr) => acc + curr.formativeTotal60, 0) / unitsWithMarks.length)
      : 0;
    const passedUnits = unitFormativeData.filter(u => u.formativeTotal60 >= 30).length;

    return { totalUnits, unitsWithMarksCount: unitsWithMarks.length, avgScore, passedUnits };
  }, [unitFormativeData]);

  const handleDownloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      const selectedYearObj = academicYears.find(y => String(y.id) === String(selectedYearId));
      const selectedSemObj = semesters.find(s => String(s.id) === String(selectedSemesterId));

      const firstReg = unitFormativeData[0]?.registration;

      const studentName = (
        (studentRecord?.first_name || studentRecord?.last_name)
          ? `${studentRecord.first_name || ''} ${studentRecord.last_name || ''}`.trim()
          : (profile?.full_name || profile?.name || 'STUDENT NAME')
      ).toUpperCase();

      const regNumber = studentRecord?.admission_number || profile?.student_id || '';
      
      const resolveProgramName = () => {
        if (studentRecord?.program_name && studentRecord.program_name !== 'N/A') return studentRecord.program_name;
        if (studentRecord?.program?.name && studentRecord.program.name !== 'N/A') return studentRecord.program.name;

        for (const u of unitFormativeData || []) {
          if (u?.registration?.unit?.program?.name) return u.registration.unit.program.name;
        }

        const regUpper = String(regNumber).toUpperCase();
        if (regUpper.startsWith('TIM') || regUpper.includes('TIM600')) {
          return 'Telecommunication & Information Technology Level Six';
        }
        if (regUpper.startsWith('THB')) {
          return 'Cosmetology & Hairdressing Level Six';
        }
        if (regUpper.startsWith('ICT')) {
          return 'Information Communication Technology Level Six';
        }

        return 'Level Six Diploma Programme';
      };

      const programName = resolveProgramName();
      const facultyName = studentRecord?.faculty_name || studentRecord?.program?.department?.faculty?.name || '';
      const departmentName = studentRecord?.department_name || studentRecord?.program?.department?.name || '';
      
      const yearOfStudy = (selectedSemObj && selectedSemObj.id !== 'all') 
        ? selectedSemObj.name 
        : (firstReg?.semester?.name || '');

      const academicYear = (selectedYearObj && selectedYearObj.id !== 'all')
        ? selectedYearObj.name
        : (firstReg?.academic_year?.name || '');

      await downloadFormativeResultSlipPdf({
        institution: institutionSettings,
        facultyName,
        departmentName,
        studentName,
        registrationNumber: regNumber,
        programName,
        yearOfStudy,
        academicYear,
        semesterName: yearOfStudy,
        unitData: unitFormativeData
      });
    } catch (err) {
      console.error('Error generating Formative Results Slip PDF:', err);
    } finally {
      setDownloadingPdf(false);
    }
  };

  const ExamCardContent = (
    !feeCheckComplete || billingStatus === null ? <div className="mx-auto max-w-xl rounded-xl border border-slate-200 bg-white p-8 text-center text-slate-600">Verifying registration and fee status…</div> : billingStatus !== 'billed' ? <div className="mx-auto max-w-xl rounded-xl border border-amber-200 bg-amber-50 p-8 text-center text-amber-900"><h1 className="text-xl font-bold">Exam Card unavailable</h1><p className="mt-2">Complete unit registration and wait for Finance to post your bill before accessing your exam card.</p></div> : bookedExamRegistrations.length === 0 ? <div className="mx-auto max-w-xl rounded-xl border border-amber-200 bg-amber-50 p-8 text-center text-amber-900"><h1 className="text-xl font-bold">Exam Card unavailable</h1><p className="mt-2">Book at least one unit for the summative examination before accessing your exam card.</p></div> : feeBalance == null || Number(feeBalance) > 0 ? <div className="mx-auto max-w-xl rounded-xl border border-amber-200 bg-amber-50 p-8 text-center text-amber-900"><h1 className="text-xl font-bold">Exam Card unavailable</h1><p className="mt-2">{feeBalance == null ? 'We could not verify your fee balance. Please contact Finance before accessing your exam card.' : `Please clear your outstanding fee balance of Ksh. ${feeBalance.toLocaleString()} before viewing or downloading your exam card.`}</p></div> :
    <div ref={examCardRef} className="mx-auto max-w-5xl border-2 border-slate-900 bg-white p-8 text-slate-900">
      <div className="mb-6 flex items-start justify-between gap-4"><div className="flex items-center gap-3"><img src={institutionSettings?.logo_url || '/logo192.png'} alt="Institution logo" className="h-16 w-16 object-contain"/><div><p className="text-sm font-semibold uppercase">{institutionSettings?.name || 'Institution'}</p><h1 className="mt-2 text-xl font-bold uppercase">Authorization to Sit University Examinations</h1></div></div>{!examDownloading && <button type="button" onClick={downloadExamCard} className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white">Download PDF</button>}</div>
      <div className="grid gap-6 md:grid-cols-2"><div className="space-y-2 text-sm"><p><b>Name:</b> {studentRecord?.full_name || [studentRecord?.first_name, studentRecord?.last_name].filter(Boolean).join(' ')}</p><p><b>Reg. No:</b> {studentRecord?.admission_number || studentRecord?.registration_number}</p><p><b>Programme:</b> {studentRecord?.program?.name}</p><p><b>Level / Module:</b> {studentRecord?.academic_level} / Module {studentRecord?.current_module}</p><div className="mt-5 flex items-end gap-4"><QRCodeSVG value={JSON.stringify({serial:`EX-${studentRecord?.id?.slice?.(0,8)||'CARD'}-${selectedSemesterId}`,student:studentRecord?.admission_number,year:academicYears.find(y=>String(y.id)===String(selectedYearId))?.name,semester:semesters.find(s=>String(s.id)===String(selectedSemesterId))?.name,units:bookedExamRegistrations.map(r=>r.unit?.code)})} size={108}/><div><p><b>Academic year:</b> {academicYears.find(y=>String(y.id)===String(selectedYearId))?.name || 'Current'}</p><p><b>Semester:</b> {semesters.find(s=>String(s.id)===String(selectedSemesterId))?.name || 'Current'}</p><p className="mt-2 font-bold text-blue-700">Exam Serial: EX-{studentRecord?.id?.slice?.(0,8)||'CARD'}-{String(selectedSemesterId).slice(0,6)}</p></div></div></div><div><table className="w-full border-collapse border border-slate-900 text-sm"><thead><tr><th className="border border-slate-900 p-2 text-left">Course Code</th><th className="border border-slate-900 p-2 text-left">Description</th><th className="border border-slate-900 p-2">Invigilator Sign</th></tr></thead><tbody>{bookedExamRegistrations.map(reg=><tr key={reg.id}><td className="border border-slate-900 p-2">{reg.unit?.code}</td><td className="border border-slate-900 p-2">{reg.unit?.name}</td><td className="border border-slate-900 p-2 h-8"/></tr>)}</tbody></table></div></div>
      <div className="mt-8 text-xs"><b>NOTES:</b><ol className="list-decimal pl-5"><li>This card must be presented to the invigilator at each examination.</li><li>Report any anomalies or errors to the Registrar.</li><li>Your registration number must appear on every answer book.</li></ol></div>
    </div>
  );

  const PageContent = examCardRequested ? ExamCardContent : (
    <div className="space-y-6">
      
      {/* Sleek Minimal Header */}
      {/* Single-line Minimalistic Toolbar */}
      <div className="flex flex-wrap lg:flex-nowrap items-center justify-between gap-3 rounded-xl border border-slate-200/80 bg-white p-3 shadow-sm">
        <div className="flex items-center gap-2 shrink-0">
          <h1 className="text-lg font-bold tracking-tight text-slate-900">Formative Assessment Results</h1>
          <span className="text-xs text-slate-500 font-mono">· {unitFormativeData.length} units</span>
        </div>

        <div className="flex flex-wrap items-center gap-2 flex-1 justify-end">
          <select
            value={selectedYearId}
            onChange={(e) => setSelectedYearId(e.target.value)}
            className="h-8 px-2.5 text-xs font-semibold rounded-lg border border-slate-200 bg-white text-slate-800 focus:ring-2 focus:ring-primary focus:outline-none cursor-pointer"
          >
            <option value="all">All Academic Years</option>
            {academicYears.map(y => (
              <option key={y.id} value={y.id}>{y.name}</option>
            ))}
          </select>

          <select
            value={selectedSemesterId}
            onChange={(e) => setSelectedSemesterId(e.target.value)}
            className="h-8 px-2.5 text-xs font-semibold rounded-lg border border-slate-200 bg-white text-slate-800 focus:ring-2 focus:ring-primary focus:outline-none cursor-pointer"
          >
            <option value="all">All Semesters / Modules</option>
            {semesters.map(s => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>

          <div className="relative w-48 min-w-[150px]">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Search unit code or name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full h-8 pl-8 pr-3 text-xs rounded-lg border border-slate-200 bg-white text-slate-800 placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:outline-none"
            />
          </div>

          <button
            onClick={handleDownloadPdf}
            disabled={downloadingPdf || unitFormativeData.length === 0}
            className="flex items-center gap-1.5 h-8 px-3 rounded-lg bg-emerald-700 hover:bg-emerald-800 active:bg-emerald-900 text-white text-xs font-semibold shadow-xs transition-all disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer shrink-0"
          >
            <Download className="w-3.5 h-3.5" />
            <span>{downloadingPdf ? 'Generating...' : 'Download Formative Results Slip'}</span>
          </button>
        </div>
      </div>

      {/* Summary KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="bg-white rounded-xl border border-slate-200/80 p-3.5 shadow-xs flex items-center justify-between">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Enrolled Units</p>
            <p className="text-xl font-extrabold text-slate-900 mt-0.5">{unitFormativeData.length}</p>
          </div>
          <div className="h-9 w-9 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center font-bold text-sm">
            {unitFormativeData.length}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200/80 p-3.5 shadow-xs flex items-center justify-between">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Average CAM Score</p>
            <p className="text-xl font-extrabold text-emerald-700 mt-0.5">
              {unitFormativeData.filter(u => u.hasMarks).length > 0 
                ? (unitFormativeData.reduce((acc, u) => acc + (u.hasMarks ? u.formativeTotal60 : 0), 0) / unitFormativeData.filter(u => u.hasMarks).length).toFixed(1) 
                : '—'} <span className="text-xs font-semibold text-slate-400">/ 60</span>
            </p>
          </div>
          <div className="h-9 w-9 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center font-bold text-sm">
            CAM
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200/80 p-3.5 shadow-xs flex items-center justify-between">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Satisfactory Rate</p>
            <p className="text-xl font-extrabold text-slate-900 mt-0.5">
              {unitFormativeData.filter(u => u.hasMarks && u.formativeTotal60 >= 30).length} <span className="text-xs font-normal text-slate-500">/ {unitFormativeData.filter(u => u.hasMarks).length || unitFormativeData.length} units</span>
            </p>
          </div>
          <div className="h-9 w-9 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center font-bold text-sm">
            {unitFormativeData.filter(u => u.hasMarks).length ? Math.round((unitFormativeData.filter(u => u.hasMarks && u.formativeTotal60 >= 30).length / unitFormativeData.filter(u => u.hasMarks).length) * 100) : 0}%
          </div>
        </div>
      </div>

      {/* Main Units Marks Table Container */}
      <div className="bg-white rounded-xl border border-slate-200/80 shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-slate-500 space-y-3">
            <RefreshCw className="w-6 h-6 animate-spin mx-auto text-emerald-700" />
            <p className="text-xs font-semibold text-slate-600">Loading formative assessment marks...</p>
          </div>
        ) : unitFormativeData.length === 0 ? (
          /* Empty State */
          <div className="p-12 text-center text-slate-500">
            <AlertCircle className="w-10 h-10 text-slate-300 mx-auto mb-2" />
            <h3 className="text-sm font-bold text-slate-800">No Unit Registrations Found</h3>
            <p className="text-xs text-slate-500 mt-1 max-w-md mx-auto">
              No enrolled units match your selected filters. Try changing the Academic Year or Semester dropdown.
            </p>
          </div>
        ) : (
          /* Re-engineered Serialized Table Layout */
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm border-collapse">
              <thead className="bg-slate-50 text-xs font-semibold uppercase text-slate-500 border-b border-slate-200/80">
                <tr>
                  <th className="w-12 px-4 py-3">#</th>
                  <th className="px-4 py-3">Unit Code</th>
                  <th className="px-4 py-3">Course / Unit Title</th>
                  {Array.from({ length: maxCamsCount }).map((_, i) => (
                    <th key={i} className="px-4 py-3 text-center whitespace-nowrap">CAM {i + 1} (/30)</th>
                  ))}
                  <th className="px-4 py-3 text-center bg-emerald-50/60 text-emerald-900 font-bold">Formative Total (/60)</th>
                  <th className="px-4 py-3 text-center">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 font-sans">
                {unitFormativeData.map((item, idx) => {
                  const cam1Obj = item.cams.find(c => c.targetCamNum === 1);
                  const cam2Obj = item.cams.find(c => c.targetCamNum === 2);
                  const cam3Obj = item.cams.find(c => c.targetCamNum === 3);
                  
                  const renderCam = (camObj) => {
                    if (!camObj) return <span className="text-slate-400 italic">—</span>;
                    if (camObj.isMissingPoe) return <span className="text-red-600 font-bold bg-red-50 px-2 py-0.5 rounded border border-red-200/80 text-[10px] tracking-wide uppercase">NP</span>;
                    if (camObj.marks !== null && camObj.marks !== undefined) {
                      return <span className="text-slate-900 font-bold bg-amber-50 px-2 py-0.5 rounded border border-amber-200/80">{camObj.marks}</span>;
                    }
                    return <span className="text-slate-400 italic">—</span>;
                  };

                  return (
                    <tr key={item.registration.id || idx} className="hover:bg-slate-50/70 transition-colors">
                      
                      {/* Serial Number */}
                      <td className="px-4 py-3 font-semibold text-slate-400">
                        {idx + 1}
                      </td>

                      {/* Unit Code */}
                      <td className="px-4 py-3 font-mono text-xs font-bold text-slate-900">
                        <span className="px-2 py-0.5 rounded bg-slate-100 border border-slate-200 text-slate-800">
                          {item.unitCode}
                        </span>
                      </td>

                      {/* Unit Title */}
                      <td className="px-4 py-3 font-medium text-slate-800">
                        {item.unitName}
                      </td>

                      {/* Dynamic CAM Columns */}
                      {Array.from({ length: maxCamsCount }).map((_, i) => {
                        const targetCamNum = i + 1;
                        const camObj = item.cams.find(c => c.targetCamNum === targetCamNum);
                        return (
                          <td key={targetCamNum} className="px-4 py-3 text-center font-mono text-xs">
                            {renderCam(camObj)}
                          </td>
                        );
                      })}

                      {/* Total Formative Score (/60) */}
                      <td className="px-4 py-3 text-center font-mono text-sm bg-emerald-50/40">
                        {item.hasMarks ? (
                          <span className="inline-flex items-center gap-1 font-extrabold text-emerald-800">
                            {item.formativeTotal60}
                            <span className="text-xs font-semibold text-emerald-600">/ 60</span>
                          </span>
                        ) : (
                          <span className="text-slate-400 text-xs italic">Pending</span>
                        )}
                      </td>

                      {/* Status */}
                      <td className="px-4 py-3 text-center">
                        {!item.hasMarks ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-slate-100 border border-slate-200 text-slate-600">
                            In Progress
                          </span>
                        ) : item.formativeTotal60 >= 30 ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-50 border border-emerald-200 text-emerald-800">
                            Satisfactory
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-rose-50 border border-rose-200 text-rose-800">
                            Needs Review
                          </span>
                        )}
                      </td>

                    </tr>
                  );
                })}
                {/* Debug Dump */}
                {window.location.search.includes('debug=true') && (
                  <tr>
                    <td colSpan="8" className="p-4 bg-slate-900 text-green-400 font-mono text-xs overflow-x-auto whitespace-pre-wrap">
                      <div className="font-bold text-white mb-2">DEBUG INFO:</div>
                      <div>
                        assessments.length: {assessments.length}
                        <br/>
                        filteredRegistrations.length: {filteredRegistrations.length}
                        <br/>
                        timelines.length: {timelines.length}
                      </div>
                      <div className="mt-2 text-yellow-300">
                        {JSON.stringify(assessments, null, 2)}
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );

  if (isStudent && resitRequested) {
    return <StudentLayout student={studentRecord}><StudentResitExamDashboard /></StudentLayout>;
  }

  if (isStudent && !examCardRequested && !resultsRequested && !formativeRequested && !historyRequested) {
    return <StudentLayout><StudentSummativeExamDashboard /></StudentLayout>;
  }

  if (isStudent && resultsRequested) {
    return <StudentLayout student={studentRecord}><ProvisionalResults /></StudentLayout>;
  }

  if (isStudent && historyRequested) {
    return <StudentLayout student={studentRecord}><AcademicProgress /></StudentLayout>;
  }

  if (isStudent && formativeRequested) {
    return <StudentLayout student={studentRecord}>{PageContent}</StudentLayout>;
  }

  if (isStudent) {
    return <StudentLayout student={studentRecord}>{PageContent}</StudentLayout>;
  }

  const DepartmentContent = <DepartmentSummativeExamDashboard />;
  return embedded ? DepartmentContent : <AdminLayout>{DepartmentContent}</AdminLayout>;
};

export default SummativeAssessmentPortal;
